import React from 'react';
import { FormShowTemplate, FormFieldsData, DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { useEmployeeService, splitFamily } from 'domain/shared/employee';
import Prepopulation from 'domain/people/employee/prepopulation/Prepopulation';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { addLocalIdToMembers } from '../shared/useFamilyData';
import { FamilyMember } from '../shared/types';
import getFamilyInfoValidationSchema from '../createupdate/familyInfoValidationSchema';
import FamilyContent from '../shared/FamilyContent';

const EmployeeFamilySummary: React.FC = () => {
  const { updateFamilyInfo, getForUpdate } = useEmployeeService();
  const employeeId = useEmployeeId();
  const { addMessage } = useMessageService('employeeFamily');

  const handleErrorsOnSubmit = () => {
    addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
  };

  if (!employeeId) return null;

  const handleSubmit = async (values: FormFieldsData): Promise<void | object> => {
    return updateFamilyInfo(employeeId, values);
  };

  const mapDataForForm = (showData: FormFieldsData) => {
    if (showData?.extendedInformation?.familyMembers) {
      const familyData = splitFamily(showData.extendedInformation.familyMembers);

      return {
        ...showData,
        extendedInformation: {
          ...showData.extendedInformation,
          children: addLocalIdToMembers(familyData.children),
          other: addLocalIdToMembers(familyData.other),
        },
      };
    }

    return showData;
  };

  const mapFamilyResponse = (response: {
    info?: { data?: { familyMembers?: FamilyMember[] } };
    artifacts?: unknown[];
  }) => {
    return {
      ...response,
      familyMembers: response?.info?.data?.familyMembers || [],
      artifacts: response?.artifacts || [],
    };
  };

  return (
    <FormShowTemplate
      getDataFunc={() => getForUpdate(employeeId)}
      submitFormFunc={handleSubmit}
      getValidationSchemaFunc={getFamilyInfoValidationSchema}
      mapDataForForm={mapDataForForm}
      messageContext="employeeFamily"
      handleErrorsOnSubmit={handleErrorsOnSubmit}
    >
      <Prepopulation mapResponse={mapFamilyResponse}>
        <FamilyContent />
      </Prepopulation>
    </FormShowTemplate>
  );
};

export default EmployeeFamilySummary;
